import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import type { PaidPlanTier } from '@tabbio/types';
import { WebDialog } from '../common/WebDialog.web';
import { useToast } from '../../hooks/useToast';
import { useLocale } from '../../hooks/useLocale';
import { useRevenueCat } from '../../hooks/useRevenueCat';
import { useCheckoutFlow } from '../../hooks/useCheckoutFlow';
import { useSubscriptionState } from '../../hooks/useSubscriptionState';
import { useSettingsModal } from '../../providers/useSettingsModal';
import { useSubscriptionDisplay } from '../../lib/subscription-display';
import {
  normalizeUpgradeContextParams,
  normalizeUpgradeModeParam,
  normalizeUpgradeTierParam,
} from '../../lib/upgrade-routing';
import {
  captureProductException,
  trackProductEvent,
} from '../../lib/product-analytics';
import type { PlanMode, Tier } from './upgrade-data';
import {
  clearPendingUpgradeContext,
  getUpgradeAnalyticsProperties,
  persistPendingUpgradeContext,
} from '../../lib/monetization-context';
import { getUpgradeAllowanceCopy } from '../../lib/upgrade-allowance-copy';
import {
  PAYWALL_TIERS,
  type PaywallDetailKey,
} from '../../lib/subscription-journey/plan-presentation';
import { getCyclePrice, getTierPrices } from '../../lib/subscription-journey/pricing';
import type { CheckoutSelection } from '../../lib/subscription-journey/checkout-flow';
import {
  PaywallComparison,
  type PaywallDetailOpenState,
} from '../subscription/PaywallComparison.web';
import { PremiumOfferMark } from '../subscription/PremiumOfferMark.web';
import {
  CheckoutReviewActions,
  CheckoutReviewBody,
} from '../subscription/CheckoutReview.web';
import {
  IMPROVE_CV_HREF,
  WelcomeActions,
  WelcomeBody,
} from '../subscription/WelcomeContent.web';
import {
  LegalLinks,
  SubButton,
  useDialogFocus,
} from '../subscription/journey-ui.web';
import {
  EmployerUpgradeContent,
  type UpgradeContentProps,
} from './EmployerUpgradeContent.web';

const TIER_NAMES: Record<PaidPlanTier, string> = { plus: 'Plus', pro: 'Pro' };

/**
 * B: the monthly premium offer (individual plans; 24 September "premium-simple"
 * design): "Get Tabbio {tier}" with the Spark/Nova mark, a Plus/Pro switch, one
 * Free-versus-plan comparison table and one "Upgrade for {price}" action, with
 * the in-place checkout review (C) and, after a verified in-place purchase,
 * the Welcome step (C2). Monthly only: the `billing` prop/param is ignored.
 * Employer mode keeps the pre-redesign feature table (`EmployerUpgradeContent`).
 */
export function UpgradeContent(props: UpgradeContentProps = {}) {
  const params = useLocalSearchParams<{ mode?: string }>();
  const resolvedContext =
    props.context ?? normalizeUpgradeContextParams(params as never);
  const planMode: PlanMode =
    resolvedContext?.planSet ?? props.mode ?? normalizeUpgradeModeParam(params.mode);
  if (planMode === 'employer') {
    return <EmployerUpgradeContent {...props} />;
  }
  return <IndividualUpgradeContent {...props} />;
}

/** The offer is monthly only; other catalog periods are separate provider offers. */
const OFFER_CYCLE = 'monthly' as const;
const DETAILS_CLOSED: PaywallDetailOpenState = { tools: false, insights: false, customization: false };

function IndividualUpgradeContent({
  context,
  tier: tierProp,
  onComplete,
  onClose,
  onSkip,
}: UpgradeContentProps) {
  const { t, i18n } = useTranslation('common');
  const { isRTL } = useLocale();
  const router = useRouter();
  const toast = useToast();
  const { syncSubscription } = useRevenueCat();
  const { catalog } = useSubscriptionState();
  const settingsModal = useSettingsModal();
  const subscriptionDisplay = useSubscriptionDisplay('web');
  const params = useLocalSearchParams<{
    tier?: string;
    billing?: string;
    placement?: string;
    entryPoint?: string;
    actionKey?: string;
    featureKey?: string;
    reason?: string;
    allowanceRemaining?: string;
    allowanceLimit?: string;
    allowanceUnit?: string;
    resetAt?: string;
    returnTo?: string;
    draftId?: string;
    resumeToken?: string;
  }>();
  const resolvedContext = context ?? normalizeUpgradeContextParams(params);
  const planMode: PlanMode = 'individual';
  const requestedTier: Tier =
    tierProp ??
    resolvedContext?.recommendedTier ??
    normalizeUpgradeTierParam(params.tier);
  const [activeTier, setActiveTier] = useState<Tier>(requestedTier);
  const billing = OFFER_CYCLE;
  // The three shared detail rows keep their open state across plan switches.
  const [openDetails, setOpenDetails] = useState<PaywallDetailOpenState>(DETAILS_CLOSED);
  const currentTier =
    subscriptionDisplay.upgradeMode === planMode ? subscriptionDisplay.tier : 'free';
  const showManageInsteadOfCheckout =
    currentTier === 'pro' || (currentTier === 'plus' && activeTier === 'plus');
  const viewedRef = useRef(false);
  const startedAtRef = useRef(0);
  const contextAnalytics = getUpgradeAnalyticsProperties(resolvedContext);
  const allowanceCopy = getUpgradeAllowanceCopy(
    resolvedContext,
    t,
    i18n.resolvedLanguage ?? i18n.language ?? 'en',
  );

  const close = useCallback(() => {
    const completionHandler = onClose ?? onComplete;
    if (completionHandler) {
      completionHandler();
      return;
    }
    if (typeof window !== 'undefined' && window.history.length > 1) {
      router.back();
      return;
    }
    router.replace('/' as never);
  }, [onClose, onComplete, router]);

  const onPurchased = useCallback(
    (selection: CheckoutSelection) => {
      trackProductEvent('paywall_purchase_completed', {
        planMode,
        tier: selection.tier,
        billingInterval: selection.cycle,
        durationMs: Math.max(Date.now() - startedAtRef.current, 0),
        surface: 'web',
        ...contextAnalytics,
      });
      // The Welcome step renders from `purchased`; nothing else to do here.
      clearPendingUpgradeContext();
    },
    [contextAnalytics, planMode],
  );
  const checkout = useCheckoutFlow({ planSet: 'individual', onPurchased });
  const status = checkout.state.status;

  useEffect(() => {
    if (currentTier === 'pro' || requestedTier === 'pro') setActiveTier('pro');
  }, [currentTier, requestedTier]);

  useEffect(() => {
    if (viewedRef.current) return;
    viewedRef.current = true;
    trackProductEvent('paywall_viewed', {
      planMode,
      activeTier,
      billingInterval: billing,
      currentTier,
      surface: 'web',
      subscriptionStateLoading: subscriptionDisplay.isLoadingSubscriptionState,
      journeyVersion: 3,
      ...contextAnalytics,
    });
  }, [
    activeTier,
    billing,
    contextAnalytics,
    currentTier,
    planMode,
    subscriptionDisplay.isLoadingSubscriptionState,
  ]);

  useEffect(() => {
    const selection = checkout.state.selection;
    const base = {
      planMode,
      tier: selection?.tier ?? activeTier,
      billingInterval: selection?.cycle ?? billing,
      durationMs: Math.max(Date.now() - startedAtRef.current, 0),
      surface: 'web' as const,
    };
    if (status === 'redirecting') {
      trackProductEvent('paywall_checkout_redirected', base);
    } else if (status === 'cancelled') {
      trackProductEvent('paywall_purchase_cancelled', base);
    } else if (status === 'error') {
      trackProductEvent('paywall_purchase_failed', {
        ...base,
        reason: checkout.state.failure ?? 'unknown',
      });
      captureProductException(new Error('Paywall checkout failed'), {
        feature: 'paywall',
        action: 'purchase',
        planMode,
        tier: base.tier,
        billingInterval: base.billingInterval,
        surface: 'web',
      });
    }
    // Status transitions only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const prices = getTierPrices(catalog, 'individual', activeTier);
  const cyclePrice = getCyclePrice(prices, billing);
  const offersPurchase = !showManageInsteadOfCheckout && Boolean(cyclePrice);
  const plusLocked = currentTier === 'pro';

  const selectTier = (tier: Tier) => {
    if (tier === activeTier || (tier === 'plus' && plusLocked)) return;
    trackProductEvent('paywall_tier_selected', {
      planMode,
      selectedTier: tier,
      previousTier: activeTier,
      currentTier,
      surface: 'web',
    });
    setActiveTier(tier);
  };

  const onSwitchKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const order = PAYWALL_TIERS.filter((tier) => !(tier === 'plus' && plusLocked));
    const index = order.indexOf(activeTier);
    const forward = isRTL ? 'ArrowLeft' : 'ArrowRight';
    const backward = isRTL ? 'ArrowRight' : 'ArrowLeft';
    let next: Tier | undefined;
    if (event.key === forward || event.key === 'ArrowDown') next = order[(index + 1) % order.length];
    else if (event.key === backward || event.key === 'ArrowUp') next = order[(index - 1 + order.length) % order.length];
    else if (event.key === 'Home') next = order[0];
    else if (event.key === 'End') next = order[order.length - 1];
    if (!next) return;
    event.preventDefault();
    selectTier(next);
    const target = event.currentTarget.querySelector<HTMLButtonElement>(`button[data-tier="${next}"]`);
    target?.focus();
  };

  const handleGet = () => {
    trackProductEvent('paywall_cta_clicked', {
      planMode,
      tier: activeTier,
      billingInterval: billing,
      currentTier,
      action: showManageInsteadOfCheckout ? 'manage' : 'purchase',
      surface: 'web',
      ...contextAnalytics,
    });
    if (subscriptionDisplay.isLoadingSubscriptionState) {
      trackProductEvent('paywall_cta_ignored', {
        planMode,
        tier: activeTier,
        billingInterval: billing,
        reason: 'subscription_state_loading',
        surface: 'web',
      });
      return;
    }
    if (showManageInsteadOfCheckout) {
      trackProductEvent('paywall_manage_opened', {
        planMode,
        currentTier,
        surface: 'web',
      });
      if (onClose) {
        onClose();
        settingsModal.open();
        return;
      }
      router.replace('/settings' as never);
      return;
    }
    if (!cyclePrice) return;
    checkout.openReview({ tier: activeTier, cycle: billing });
  };

  const handlePurchase = () => {
    if (checkout.isBusy) return;
    startedAtRef.current = Date.now();
    // Hosted web checkout leaves the page; the return screen resumes the
    // blocked action from this parked context.
    if (resolvedContext) persistPendingUpgradeContext(resolvedContext);
    void checkout.purchase();
  };

  // Picker "Restore": sync; an already-paid account sees the manage state.
  const handleRestore = async () => {
    try {
      await syncSubscription({ planSet: 'individual', companyId: null });
      toast.success(t('settings.subscriptionSynced'));
    } catch {
      toast.error(t('settings.syncFailed'), t('settings.syncFailedMessage'));
    }
  };

  const finishWelcome = (destination: 'improve' | 'later') => {
    const returnTo = resolvedContext?.returnTo;
    close();
    if (destination === 'improve') {
      router.push(IMPROVE_CV_HREF as never);
      return;
    }
    if (returnTo) {
      trackProductEvent('blocked_action_resumed', {
        surface: 'web',
        ...contextAnalytics,
      });
      router.replace(returnTo as never);
    }
  };

  const isReview = status !== 'picker' && status !== 'purchased';
  const purchasedTier =
    status === 'purchased' ? (checkout.state.selection?.tier ?? null) : null;
  // The dialog's <h2> is its accessible name: the phone sheet is close-only,
  // but on web the short screen title keeps the chrome usable.
  const title = purchasedTier
    ? t('subscription.welcome.title')
    : isReview
      ? t('subscription.checkout.title')
      : t('subscription.paywall.screenTitle');
  const canGoBack =
    isReview && !checkout.isBusy && status !== 'pending' && status !== 'checking';

  const footer = (
    <div dir={isRTL ? 'rtl' : 'ltr'} className="tabbio-sub tabbio-sub-actions">
      {purchasedTier ? (
        <WelcomeActions
          onImprove={() => finishWelcome('improve')}
          onNotNow={() => finishWelcome('later')}
        />
      ) : isReview ? (
        <CheckoutReviewActions
          state={checkout.state}
          onPurchase={handlePurchase}
          onRestore={() => void checkout.restore()}
          onCheckAgain={() => void checkout.checkAgain()}
          onBackToTabbio={close}
        />
      ) : (
        <>
          <SubButton
            className="is-offer"
            onClick={handleGet}
            disabled={subscriptionDisplay.isLoadingSubscriptionState}
          >
            {subscriptionDisplay.isLoadingSubscriptionState
              ? t('settings.checkingSubscription')
              : showManageInsteadOfCheckout
                ? subscriptionDisplay.subscriptionRowLabel
                : t('subscription.paywall.upgradeFor', { price: cyclePrice?.chargeDisplay ?? '' })}
          </SubButton>
          {offersPurchase ? (
            <p className="tabbio-sub-disclosure is-offer">
              {t('subscription.paywall.disclosureMonthly')}
            </p>
          ) : null}
          {onSkip ? (
            <SubButton kind="text" onClick={onSkip}>
              {t('upgrade.continueFree')}
            </SubButton>
          ) : null}
          <LegalLinks onRestore={() => void handleRestore()} />
        </>
      )}
    </div>
  );

  return (
    <WebDialog
      title={title}
      onClose={close}
      onBack={canGoBack ? () => checkout.backToPicker() : undefined}
      maxWidth={560}
      // The upgrade modal can be summoned from inside another dialog (e.g. the
      // create-skill dialog at zIndex 9999), so it paints above that layer but
      // below the alert host (100000).
      zIndex={10000}
      footer={footer}
    >
      <PaywallBody key={purchasedTier ? 'welcome' : isReview ? 'review' : 'picker'} onEscape={close}>
        {purchasedTier ? (
          <WelcomeBody tier={purchasedTier} />
        ) : isReview ? (
          <CheckoutReviewBody
            state={checkout.state}
            prices={getTierPrices(
              catalog,
              'individual',
              checkout.state.selection?.tier ?? activeTier,
            )}
            onChangePlan={() => {
              if (canGoBack) checkout.backToPicker();
            }}
          />
        ) : (
          <>
            <div className="tabbio-sub-premium-offer" aria-live="polite" aria-atomic="true">
              <PremiumOfferMark tier={activeTier} size={48} />
              <h1>{t('subscription.paywall.heading', { tier: TIER_NAMES[activeTier] })}</h1>
              <p>{t(`subscription.paywall.subtitle.${activeTier}`)}</p>
            </div>

            {allowanceCopy ? (
              <p className="tabbio-sub-info" style={{ marginTop: 0, marginBottom: 16 }}>
                {allowanceCopy}
              </p>
            ) : null}

            <div
              className="tabbio-sub-seg is-premium-switch"
              role="radiogroup"
              aria-label={t('subscription.paywall.tierGroupLabel')}
              style={{ ['--selected' as string]: activeTier === 'pro' ? 1 : 0 }}
              onKeyDown={onSwitchKeyDown}
            >
              {PAYWALL_TIERS.map((tier) => {
                const locked = tier === 'plus' && plusLocked;
                const selected = activeTier === tier;
                return (
                  <button
                    key={tier}
                    type="button"
                    role="radio"
                    data-tier={tier}
                    aria-checked={selected}
                    aria-disabled={locked || undefined}
                    tabIndex={selected ? 0 : -1}
                    onClick={() => selectTier(tier)}
                  >
                    {TIER_NAMES[tier]}
                  </button>
                );
              })}
            </div>

            <PaywallComparison
              tier={activeTier}
              openDetails={openDetails}
              onToggleDetail={(detail: PaywallDetailKey, open: boolean) =>
                setOpenDetails((current) =>
                  current[detail] === open ? current : { ...current, [detail]: open },
                )
              }
            />
          </>
        )}
      </PaywallBody>
    </WebDialog>
  );
}

function PaywallBody({
  children,
  onEscape,
}: {
  children: React.ReactNode;
  onEscape: () => void;
}) {
  const { isRTL } = useLocale();
  const ref = useRef<HTMLDivElement>(null);
  useDialogFocus(ref, onEscape);
  return (
    <div ref={ref} dir={isRTL ? 'rtl' : 'ltr'} className="tabbio-sub tabbio-sub-step-enter">
      {children}
    </div>
  );
}
